import { Faction, TextColor, TextFamily, TextWeight } from "@og-eternity/client-lib"
import { AppColor, ButtonColor } from "../types/styling"
import { Numeric } from "../utils/types"
import Colors from "./Colors.json"

export const ThemeColors = Colors
export type CustomColor = keyof typeof Colors

export const AppButtonColors: Record<AppColor, ButtonColor> = {
  "primary": "blue",
  "secondary": "blue-dark",
}

export const mkHexNumber = (stringHex: string) => parseInt(stringHex.replace('#', ''), 16)
export const HexColors: Numeric<typeof Colors> = {
  background: {
    DEFAULT: mkHexNumber(Colors.background.DEFAULT),
  },
  rarity: {
    common: mkHexNumber(Colors.rarity.common),
    rare: mkHexNumber(Colors.rarity.rare),
    epic: mkHexNumber(Colors.rarity.epic),
    legendary: mkHexNumber(Colors.rarity.legendary),
    mythic: mkHexNumber(Colors.rarity.mythic),
    gen0: mkHexNumber(Colors.rarity.gen0),
  },
  accent: {
    light: mkHexNumber(Colors.accent.light),
    DEFAULT: mkHexNumber(Colors.accent.DEFAULT),
    dark: mkHexNumber(Colors.accent.dark),
  },
  black: {
    DEFAULT: mkHexNumber(Colors.black.DEFAULT),
    almost: mkHexNumber(Colors.black.almost),
    light: mkHexNumber(Colors.black.light)
  },
  red: {
    warning: mkHexNumber(Colors.red.warning),
  },
  gold: {
    light: mkHexNumber(Colors.gold.light),
    DEFAULT: mkHexNumber(Colors.gold.DEFAULT),
    dark: mkHexNumber(Colors.gold.dark)
  },
  faction: {
    ally: mkHexNumber(Colors.faction.ally),
    enemy: mkHexNumber(Colors.faction.enemy),
    game: mkHexNumber(Colors.faction.game),
  },
  placement: {
    first: mkHexNumber(Colors.placement.first),
    second: mkHexNumber(Colors.placement.second),
    third: mkHexNumber(Colors.placement.third),
    player: mkHexNumber(Colors.placement.player),
  },
  status: {
    error: mkHexNumber(Colors.status.error),
    warning: mkHexNumber(Colors.status.warning),
    success: mkHexNumber(Colors.status.success),
    turnmeter: mkHexNumber(Colors.status.turnmeter),
    damage: mkHexNumber(Colors.status.damage),
    heal: mkHexNumber(Colors.status.heal),
    buffed: mkHexNumber(Colors.status.buffed),
    fatal: mkHexNumber(Colors.status.fatal),
  },
  network: {
    avax: mkHexNumber(Colors.network.avax),
    metis: mkHexNumber(Colors.network.metis),
    polygon: mkHexNumber(Colors.network.polygon),
    polygonbright: mkHexNumber(Colors.network.polygonbright),
    none: mkHexNumber(Colors.network.none),
  },
  attributes: {
    health: mkHexNumber(Colors.attributes.health),
    vitality: mkHexNumber(Colors.attributes.vitality),
    power: mkHexNumber(Colors.attributes.power),
    focus: mkHexNumber(Colors.attributes.focus),
    will: mkHexNumber(Colors.attributes.will),
    agility: mkHexNumber(Colors.attributes.agility),
    haste: mkHexNumber(Colors.attributes.haste),
    dodge: mkHexNumber(Colors.attributes.dodge),
    percentHealingIncrease: mkHexNumber(Colors.attributes.percentHealingIncrease),
    accuracy: mkHexNumber(Colors.attributes.accuracy),
    armor: mkHexNumber(Colors.attributes.armor),
    toughness: mkHexNumber(Colors.attributes.toughness),
    poison: mkHexNumber(Colors.attributes.poison),
    criticalchance: mkHexNumber(Colors.attributes.criticalchance),
    criticalmult: mkHexNumber(Colors.attributes.criticalmult),
    criticalDamageReductionPercent: mkHexNumber(Colors.attributes.criticalDamageReductionPercent),
    physicalresist: mkHexNumber(Colors.attributes.physicalresist),
    holyresist: mkHexNumber(Colors.attributes.holyresist),
    deathresist: mkHexNumber(Colors.attributes.deathresist),
    chaosresist: mkHexNumber(Colors.attributes.chaosresist),
    fireresist: mkHexNumber(Colors.attributes.fireresist),
    frostresist: mkHexNumber(Colors.attributes.frostresist),
    natureresist: mkHexNumber(Colors.attributes.natureresist),
    sonicresist: mkHexNumber(Colors.attributes.sonicresist),
    default: mkHexNumber(Colors.attributes.default),
  },
  vfx: {
    black: mkHexNumber(Colors.vfx.black),
    white: mkHexNumber(Colors.vfx.white),
    red: mkHexNumber(Colors.vfx.red),
    green: mkHexNumber(Colors.vfx.green),
    blue: mkHexNumber(Colors.vfx.blue),
    purple: mkHexNumber(Colors.vfx.purple),
    pink: mkHexNumber(Colors.vfx.pink),
    orange: mkHexNumber(Colors.vfx.orange),
    yellow: mkHexNumber(Colors.vfx.yellow),
    brown: mkHexNumber(Colors.vfx.brown),
    gray: mkHexNumber(Colors.vfx.gray),
    grayDark: mkHexNumber(Colors.vfx.grayDark),
  },
  social: {
    Discord: mkHexNumber(Colors.social.Discord)
  }
}

export const FactionColors: Record<Faction, { value: string, hex: number }> = {
  [Faction.Ally]: {
    value: Colors.faction.ally,
    hex: HexColors.faction.ally,
  },
  [Faction.Enemy]: {
    value: Colors.faction.enemy,
    hex: HexColors.faction.enemy,
  },
  [Faction.Game]: {
    value: Colors.faction.game,
    hex: HexColors.faction.game,
  }
}



export const TextFamilyCSS: Record<TextFamily, string> = {
  "roboto": "font-text",
  "illegibleChickenScratch": "font-title",
}

export const TextFamilies: Record<TextFamily, string> = {
  "roboto": "Roboto",
  "illegibleChickenScratch": "Grenze",
}

export const TextWeightCSS: Record<TextWeight, string> = {
  "light": "font-light",
  "normal": "font-normal",
  "bold": "font-bold",
}

const mkColorStyle = (key: keyof typeof Colors["vfx"]) => ({
  color: Colors.vfx[key],
  fontCSS: `text-vfx-${key}`,
})

export type TextColorStyle = ReturnType<typeof mkColorStyle>
export const TextColorStyles: Record<TextColor, TextColorStyle> = {
  black: mkColorStyle('black'),
  white: mkColorStyle('white'),
  blue: mkColorStyle('blue'),
  brown: mkColorStyle('brown'),
  green: mkColorStyle('green'),
  purple: mkColorStyle('purple'),
  red: mkColorStyle('red'),
  yellow: mkColorStyle('yellow'),
}